"use client";

import { useState, useEffect, useCallback } from "react";
import { getAdminHeaders } from "@/lib/artworkApi";
import { useToast } from "@/components/shared/Toast";

// 業主後台筆記本 — 便利貼風視覺(粉彩色循環、輕旋轉、紙張陰影)
// 設計:像 Apple Stickies / Google Keep,讓記事本身是一種享受。

const PALETTE = [
  { bg: "#FFF6BD", accent: "#E0C200", name: "yellow" }, // 經典便利貼黃
  { bg: "#FFE0E9", accent: "#D6688A", name: "pink" }, // 粉紅
  { bg: "#D6F0FF", accent: "#4596C7", name: "blue" }, // 天藍
  { bg: "#D7F5DC", accent: "#5DAA6E", name: "green" }, // 草綠
  { bg: "#E8DEFF", accent: "#7E62C0", name: "purple" }, // 薰衣紫
  { bg: "#FFE2C4", accent: "#D38241", name: "orange" }, // 暖橘
];

// id 格式:note-{ms timestamp}-{rand5}。取 ms 做穩定種子,毫秒等距 → 色彩平均分布
function seedFromId(id) {
  const m = id.match(/-(\d+)-/);
  return m ? parseInt(m[1], 10) : id.length;
}

function colorFor(id) {
  return PALETTE[seedFromId(id) % PALETTE.length];
}

// 微小旋轉,讓便利貼像隨手貼上 — -2.5° ~ +2.5°
function rotateFor(id) {
  return (((seedFromId(id) * 7) % 50) - 25) / 10;
}

export default function AdminNotes() {
  const showToast = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, title, body }
  const [newDraft, setNewDraft] = useState(null); // 新增中的草稿
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/admin/notes", { headers: getAdminHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data)) setNotes(data);
      })
      .catch(() => {
        if (!cancelled) showToast("載入筆記失敗");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const startNew = useCallback(() => {
    setNewDraft({ title: "", body: "" });
  }, []);

  const cancelNew = useCallback(() => {
    setNewDraft(null);
  }, []);

  const saveNew = useCallback(async () => {
    if (!newDraft) return;
    if (!newDraft.title.trim() && !newDraft.body.trim()) {
      cancelNew();
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/admin/notes", {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify(newDraft),
      });
      if (!res.ok) throw new Error("新增失敗");
      const created = await res.json();
      setNotes((prev) => [created, ...prev]);
      setNewDraft(null);
      showToast("✓ 已新增");
    } catch {
      showToast("新增失敗,請重試");
    } finally {
      setSaving(false);
    }
  }, [newDraft, cancelNew, showToast]);

  const startEdit = useCallback((note) => {
    setEditing({ id: note.id, title: note.title, body: note.body });
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/notes/${editing.id}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ title: editing.title, body: editing.body }),
      });
      if (!res.ok) throw new Error("儲存失敗");
      const updated = await res.json();
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setEditing(null);
      showToast("✓ 已儲存");
    } catch {
      showToast("儲存失敗,請重試");
    } finally {
      setSaving(false);
    }
  }, [editing, showToast]);

  const cancelEdit = useCallback(() => setEditing(null), []);

  const toggleDone = useCallback(
    async (note) => {
      const nextDone = !note.isDone;
      // optimistic
      setNotes((prev) =>
        prev.map((n) => (n.id === note.id ? { ...n, isDone: nextDone } : n)),
      );
      try {
        const res = await fetch(`/api/admin/notes/${note.id}`, {
          method: "PATCH",
          headers: getAdminHeaders(),
          body: JSON.stringify({ isDone: nextDone }),
        });
        if (!res.ok) throw new Error("更新失敗");
      } catch {
        // revert
        setNotes((prev) =>
          prev.map((n) => (n.id === note.id ? { ...n, isDone: !nextDone } : n)),
        );
        showToast("更新失敗");
      }
    },
    [showToast],
  );

  const removeNote = useCallback(
    async (note) => {
      if (
        !confirm(
          `確定刪除這張筆記嗎?\n\n「${note.title || note.body.slice(0, 30)}」`,
        )
      )
        return;
      // optimistic
      const backup = notes;
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      try {
        const res = await fetch(`/api/admin/notes/${note.id}`, {
          method: "DELETE",
          headers: getAdminHeaders(),
        });
        if (!res.ok) throw new Error();
        showToast("已刪除");
      } catch {
        setNotes(backup);
        showToast("刪除失敗");
      }
    },
    [notes, showToast],
  );

  return (
    <section className="admin-notes" aria-labelledby="admin-notes-heading">
      <div className="admin-notes__header">
        <h2 id="admin-notes-heading" className="admin-notes__title">
          📝 我的筆記
        </h2>
        <button
          type="button"
          onClick={startNew}
          disabled={!!newDraft}
          className="admin-notes__add-btn"
          aria-label="新增筆記"
        >
          + 新增
        </button>
      </div>

      {loading && <div className="admin-notes__loading">載入中…</div>}

      {!loading && (
        <div className="admin-notes__board">
          {/* 新增草稿便利貼(優先顯示) */}
          {newDraft && (
            <div
              className="admin-notes__card admin-notes__card--draft"
              style={{
                "--note-bg": "#FFFAF0",
                "--note-accent": "#999",
                "--note-rotate": "0deg",
              }}
            >
              <input
                type="text"
                autoFocus
                value={newDraft.title}
                onChange={(e) =>
                  setNewDraft((d) => ({ ...d, title: e.target.value }))
                }
                placeholder="標題(選填)"
                className="admin-notes__input-title"
                disabled={saving}
                maxLength={200}
              />
              <textarea
                value={newDraft.body}
                onChange={(e) =>
                  setNewDraft((d) => ({ ...d, body: e.target.value }))
                }
                placeholder="想記什麼..."
                className="admin-notes__input-body"
                rows={5}
                disabled={saving}
                maxLength={5000}
              />
              <div className="admin-notes__card-actions">
                <button
                  type="button"
                  onClick={saveNew}
                  disabled={saving}
                  className="admin-notes__action admin-notes__action--primary"
                >
                  {saving ? "儲存中…" : "✓ 儲存"}
                </button>
                <button
                  type="button"
                  onClick={cancelNew}
                  disabled={saving}
                  className="admin-notes__action"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          {/* 既有筆記 */}
          {notes.map((note) => {
            const palette = colorFor(note.id);
            const rotate = rotateFor(note.id);
            const isThisEditing = editing?.id === note.id;
            return (
              <div
                key={note.id}
                className={`admin-notes__card ${note.isDone ? "is-done" : ""} ${isThisEditing ? "is-editing" : ""}`}
                style={{
                  "--note-bg": palette.bg,
                  "--note-accent": palette.accent,
                  "--note-rotate": isThisEditing ? "0deg" : `${rotate}deg`,
                }}
              >
                {isThisEditing ? (
                  <>
                    <input
                      type="text"
                      autoFocus
                      value={editing.title}
                      onChange={(e) =>
                        setEditing((d) => ({ ...d, title: e.target.value }))
                      }
                      placeholder="標題(選填)"
                      className="admin-notes__input-title"
                      disabled={saving}
                      maxLength={200}
                    />
                    <textarea
                      value={editing.body}
                      onChange={(e) =>
                        setEditing((d) => ({ ...d, body: e.target.value }))
                      }
                      placeholder="想記什麼..."
                      className="admin-notes__input-body"
                      rows={5}
                      disabled={saving}
                      maxLength={5000}
                    />
                    <div className="admin-notes__card-actions">
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={saving}
                        className="admin-notes__action admin-notes__action--primary"
                      >
                        {saving ? "儲存中…" : "✓ 儲存"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={saving}
                        className="admin-notes__action"
                      >
                        取消
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="admin-notes__card-top">
                      <button
                        type="button"
                        onClick={() => toggleDone(note)}
                        className={`admin-notes__check ${note.isDone ? "is-on" : ""}`}
                        aria-label={note.isDone ? "取消完成" : "標記完成"}
                        title={note.isDone ? "取消完成" : "標記完成"}
                      >
                        {note.isDone ? "✓" : ""}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeNote(note)}
                        className="admin-notes__remove"
                        aria-label="刪除這張筆記"
                        title="刪除"
                      >
                        ×
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => startEdit(note)}
                      className="admin-notes__card-content"
                      aria-label={`編輯筆記:${note.title || note.body.slice(0, 30)}`}
                    >
                      {note.title && (
                        <h3 className="admin-notes__card-title">
                          {note.title}
                        </h3>
                      )}
                      {note.body && (
                        <p className="admin-notes__card-body">{note.body}</p>
                      )}
                      {!note.title && !note.body && (
                        <p className="admin-notes__card-empty">(空白筆記)</p>
                      )}
                    </button>
                  </>
                )}
              </div>
            );
          })}

          {/* 空狀態 */}
          {!newDraft && notes.length === 0 && (
            <div className="admin-notes__empty">
              <span className="admin-notes__empty-emoji" aria-hidden="true">
                ✏️
              </span>
              <p className="admin-notes__empty-text">
                還沒有任何筆記
                <br />
                <small>點上面「+ 新增」記下第一則想法</small>
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
